import { Link } from 'react-router-dom';
import { X, Phone, MessageSquare, Store, Shield, Check, Minus, TrendingDown, Crown, Star } from 'lucide-react';
import { Button } from './ui/button';
import { useCompare } from '../config/compareContext';
import { useLanguage } from '../config/language';
import { useTheme } from '../config/theme';
import { PriceDisplay } from './PriceDisplay';
import { useOperatorView } from '../hooks/usePhoneOperators';
import OperatorLogo from './OperatorLogo';
import PhoneNumberLockup from './PhoneNumberLockup';
import { getDisplayNumber } from '../utils/digitMask';

const OperatorBadge = ({ item, isAr }) => {
  const op = useOperatorView(item);
  const name = isAr ? op.nameAr : op.nameEn;
  return (
    <div className="flex flex-col items-center gap-1" data-testid={`compare-operator-${item.id}`}>
      <OperatorLogo src={op.logo} name={name} accent={op.accent} className="h-6 w-6" letterClass="text-xs" />
      <span className="text-xs font-bold" style={{ color: op.accent }}>{name || '—'}</span>
    </div>
  );
};

export default function CompareModal() {
  const { compareItems, showCompareModal, closeCompareModal, removeFromCompare, clearCompare } = useCompare();
  const { language } = useLanguage();
  const { isDarkMode } = useTheme();
  const isAr = language === 'ar';

  if (!showCompareModal) return null;

  // Find lowest price for highlighting (only among numbers that actually show a price)
  const priced = compareItems.filter((item) => item.price > 0 && !item.priceOnRequest).map((item) => item.price);
  const lowestPrice = priced.length > 1 ? Math.min(...priced) : null;
  
  // Comparison attributes
  const attributes = [
    { 
      key: 'operator', 
      label: isAr ? 'المشغل' : 'Operator',
      render: (item) => <OperatorBadge item={item} isAr={isAr} />
    },
    { 
      key: 'price', 
      label: isAr ? 'السعر' : 'Price',
      render: (item) => (
        <div className="flex flex-col items-center">
          <span className={`text-lg font-bold ${item.price === lowestPrice ? 'text-green-500' : 'text-[#1E9BD9]'}`}>
            <PriceDisplay price={item.price} />
          </span>
          {item.price === lowestPrice && (
            <span className="text-xs text-green-500 flex items-center gap-1 mt-1">
              <TrendingDown className="h-3 w-3" />
              {isAr ? 'الأقل سعراً' : 'Lowest'}
            </span>
          )}
        </div>
      )
    },
    { 
      key: 'negotiable', 
      label: isAr ? 'قابل للتفاوض' : 'Negotiable',
      render: (item) => item.negotiable ? (
        <span className="flex items-center gap-1 text-green-500">
          <Check className="h-4 w-4" />
          {isAr ? 'نعم' : 'Yes'}
        </span>
      ) : (
        <span className="flex items-center gap-1 text-gray-400">
          <Minus className="h-4 w-4" />
          {isAr ? 'لا' : 'No'}
        </span>
      )
    },
    { 
      key: 'category', 
      label: isAr ? 'الفئة' : 'Category',
      render: (item) => {
        const categories = {
          golden: { label: isAr ? 'ذهبي' : 'Golden', icon: Crown, color: 'text-amber-500' },
          silver: { label: isAr ? 'فضي' : 'Silver', icon: Star, color: 'text-gray-400' },
          special: { label: isAr ? 'مميز' : 'Special', icon: Star, color: 'text-purple-500' },
          vip: { label: 'VIP', icon: Crown, color: 'text-yellow-500' },
        };
        const cat = categories[item.category] || { label: isAr ? 'عادي' : 'Normal', icon: Minus, color: 'text-gray-400' };
        const Icon = cat.icon;
        return (
          <span className={`flex items-center gap-1 ${cat.color}`}>
            <Icon className="h-4 w-4" />
            {cat.label}
          </span>
        );
      }
    },
    { 
      key: 'store', 
      label: isAr ? 'المتجر' : 'Store',
      render: (item) => (
        <div className="flex flex-col items-center gap-1">
          {item.store_logo && (
            <img src={item.store_logo} alt="" className="w-8 h-8 rounded-full object-cover" />
          )}
          <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            {item.store_name || '-'}
          </span>
          {item.store_verified && (
            <Shield className="h-4 w-4 text-[#1E9BD9]" />
          )}
        </div>
      )
    },
  ];

  const handleWhatsApp = (item) => {
    const message = isAr 
      ? `مرحباً، أنا مهتم بالرقم ${item.number} المعروض بسعر ${item.price} درهم`
      : `Hi, I'm interested in the number ${item.number} listed at ${item.price} AED`;
    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank');
  };

  return (
    <div 
      className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={closeCompareModal}
      data-testid="compare-modal"
    >
      <div 
        className={`w-full max-w-5xl max-h-[90vh] overflow-auto rounded-2xl shadow-2xl ${
          isDarkMode ? 'bg-[#0a0a0a] border border-white/10' : 'bg-white'
        }`}
        onClick={(e) => e.stopPropagation()}
        dir={isAr ? 'rtl' : 'ltr'}
      >
        {/* Header */}
        <div className={`sticky top-0 z-10 flex items-center justify-between p-6 border-b ${
          isDarkMode ? 'bg-[#0a0a0a] border-white/10' : 'bg-white border-gray-100'
        }`}>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#1E9BD9] to-[#0284C7] flex items-center justify-center">
              <Phone className="h-6 w-6 text-white" />
            </div>
            <div>
              <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-[#0f172a]'}`}>
                {isAr ? 'مقارنة الأرقام' : 'Compare Numbers'}
              </h2>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {compareItems.length} {isAr ? 'أرقام للمقارنة' : 'numbers to compare'}
              </p>
            </div>
          </div>
          <button 
            onClick={closeCompareModal}
            className={`p-2 rounded-full transition-colors ${
              isDarkMode ? 'hover:bg-white/10 text-gray-400' : 'hover:bg-gray-100 text-gray-500'
            }`}
            data-testid="close-compare-modal"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Comparison Table */}
        <div className="p-6 overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr>
                <th className={`text-start p-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}></th>
                {compareItems.map((item) => (
                  <th key={item.id} className="p-4 min-w-[200px]">
                    <div className={`relative rounded-xl p-4 ${
                      isDarkMode ? 'bg-[#141414] border border-white/10' : 'bg-gray-50 border border-gray-100'
                    }`}>
                      {/* Remove Button */}
                      <button 
                        onClick={() => removeFromCompare(item.id)}
                        className={`absolute top-2 ${isAr ? 'left-2' : 'right-2'} p-1 rounded-full transition-colors ${
                          isDarkMode 
                            ? 'hover:bg-red-500/20 text-gray-500 hover:text-red-400' 
                            : 'hover:bg-red-50 text-gray-400 hover:text-red-500'
                        }`}
                      >
                        <X className="h-4 w-4" />
                      </button>
                      
                      {/* Number */}
                      <div className="mb-2" dir="ltr">
                        <PhoneNumberLockup value={getDisplayNumber(item)} size="sm" tone={isDarkMode ? 'light' : 'dark'} testId={`compare-number-${item.id}`} />
                      </div>
                      
                      {/* Lowest Price Badge */}
                      {item.price === lowestPrice && (
                        <div className="absolute -top-2 start-1/2 -translate-x-1/2 px-3 py-1 bg-green-500 text-white text-xs font-bold rounded-full">
                          {isAr ? 'الأفضل سعراً' : 'Best Price'}
                        </div>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {attributes.map((attr, index) => (
                <tr 
                  key={attr.key}
                  className={index % 2 === 0 ? (isDarkMode ? 'bg-white/5' : 'bg-gray-50/50') : ''}
                >
                  <td className={`p-4 font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    {attr.label}
                  </td>
                  {compareItems.map((item) => (
                    <td key={item.id} className="p-4 text-center">
                      {attr.render(item)}
                    </td>
                  ))}
                </tr>
              ))}
              
              {/* Action Row */}
              <tr>
                <td className={`p-4 font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  {isAr ? 'إجراءات' : 'Actions'}
                </td>
                {compareItems.map((item) => (
                  <td key={item.id} className="p-4">
                    <div className="flex flex-col gap-2">
                      <Button
                        className="w-full bg-[#25D366] hover:bg-[#20BD5A] text-white"
                        onClick={() => handleWhatsApp(item)}
                      >
                        <MessageSquare className="h-4 w-4 me-2" />
                        {isAr ? 'واتساب' : 'WhatsApp'}
                      </Button>
                      
                      {item.store_id && (
                        <Link to={`/store/${item.store_id}`} onClick={closeCompareModal}>
                          <Button
                            variant="outline"
                            className={`w-full ${
                              isDarkMode 
                                ? 'border-white/20 text-white hover:bg-white/10' 
                                : 'border-gray-200 text-gray-700 hover:bg-gray-50'
                            }`}
                          >
                            <Store className="h-4 w-4 me-2" />
                            {isAr ? 'زيارة المتجر' : 'Visit Store'}
                          </Button>
                        </Link>
                      )}
                    </div>
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className={`sticky bottom-0 flex items-center justify-between p-6 border-t ${
          isDarkMode ? 'bg-[#0a0a0a] border-white/10' : 'bg-white border-gray-100'
        }`}>
          <Button
            variant="outline"
            onClick={clearCompare}
            className={isDarkMode ? 'border-white/20 text-gray-400' : 'border-gray-200 text-gray-500'}
          >
            {isAr ? 'مسح الكل' : 'Clear All'}
          </Button>
          
          <Button
            onClick={closeCompareModal}
            className="bg-[#1E9BD9] hover:bg-[#0284C7] text-white px-8"
          >
            {isAr ? 'إغلاق' : 'Close'}
          </Button>
        </div>
      </div>
    </div>
  );
}
